import { Button } from '@/app/components/Button';
import TermsAndConditions from '@/app/components/TermsAndConditions';
import TextField from '@/app/components/TextField';
import { capitaliseFirstChar } from '@/app/utils/capitaliseFirstChar';
import yup from '@/app/utils/customisedYup';
import { useAnalytics } from '@/app/utils/useAnalytics';
import { postLeadforms } from '@/app/utils/useRequest';
import {
  emailLengthHint,
  firstNameLengthHint,
  lastNameLengthHint,
} from '@/modules/RegistrationFlow/steps-old/RegisterBasicInfo/RegisterBasicInfo';
import styles from '@/modules/SalaryGuide/SalaryGuide.module.scss';
import { yupResolver } from '@hookform/resolvers';
import { Controller, useForm } from 'react-hook-form';

import DownloadIcon from '@material-ui/icons/SaveAlt';

const registerSchema = yup.object().shape({
  email: yup.string().trim().max(80, emailLengthHint).required().emailValidation(),
  firstName: yup.string().trim().max(40, firstNameLengthHint).required(),
  lastName: yup.string().trim().max(80, lastNameLengthHint).required(),
  agreeTerms: yup.boolean().required('You must agree to the terms and conditions'),
});

export const SalaryGuideForm = () => {
  const track = useAnalytics();
  const { handleSubmit, errors, control, formState, watch } = useForm({
    resolver: yupResolver(registerSchema),
    reValidateMode: 'onBlur',
    mode: 'onBlur',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      agreeTerms: false,
    },
  });

  const onSubmit = async data => {
    const { firstName, lastName, email } = data;
    const payload = {
      // API expects pascal case keys
      FirstName: firstName,
      LastName: lastName,
      Email: email,
      Lead_Source__c: 'jmr_au',
      Last_Whitepaper_Download__c: 'AU Salary Guide',
      Last_Whitepaper_Downloaded_Date__c: new Date().toISOString(),
      HasOptedOutOfEmail: 'false',
      Subscribed_To__c:
        'Medrecruit Jobs;Recruiter Communications;Medrecruit News and Announcements;Medworld News and Announcements',
    };
    // format request body i.e. FirstName=Ben&LastName=Smith&Email=email@example.com
    const formBody = new URLSearchParams(payload).toString();

    postLeadforms(formBody); // note - errors are logged and caught in postLeadforms - makeRequestAndNoticeErrors

    // open the PDF in a new tab
    const url =
      'https://med-cms-bucket-production.s3.ap-southeast-2.amazonaws.com/AU_Salary_Guide_ee8eb3d548.pdf';
    const newTab = window.open(url, '_blank', 'noopener');
    newTab?.focus();
  };

  const submitEnabled = !formState.isSubmitting && formState.isValid && watch('agreeTerms');

  return (
    <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="firstName"
        control={control}
        defaultValue=""
        render={({ onChange, onBlur, value, name }) => (
          <TextField
            name={name}
            value={value}
            onChange={e => {
              const val = capitaliseFirstChar(e.target.value);
              onChange(val);
            }}
            onBlur={onBlur}
            label="First name"
            required
            error={!!errors.firstName}
            errorMessage={errors.firstName?.message}
            type="text"
          />
        )}
      />

      <Controller
        name="lastName"
        control={control}
        defaultValue=""
        render={({ onChange, onBlur, value, name }) => (
          <TextField
            name={name}
            value={value}
            onChange={e => {
              const val = capitaliseFirstChar(e.target.value);
              onChange(val);
            }}
            onBlur={onBlur}
            label="Last name"
            required
            error={!!errors.lastName}
            errorMessage={errors.lastName?.message}
            type="text"
          />
        )}
      />

      <Controller
        name="email"
        control={control}
        defaultValue=""
        render={({ onChange, onBlur, value, name }) => (
          <TextField
            name={name}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            label="Email address"
            required
            error={!!errors.email}
            errorMessage={errors.email?.message}
            type="text"
          />
        )}
      />

      <TermsAndConditions
        control={control}
        error={!!errors.agreeTerms}
        errorMessage={errors.agreeTerms?.message}
      />

      <Button
        type="submit"
        disabled={!submitEnabled}
        trackFunction={eventProps => {
          track('Click', {
            ...eventProps,
            action: 'Download',
          });
        }}
        iconRight={<DownloadIcon fontSize="small" />}
      >
        Download
      </Button>
    </form>
  );
};
